// React Dependencies
import { Navigate, useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import moment from "moment"

// MUI Imports //
import Table from "@mui/material/Table";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableSortLabel from "@mui/material/TableSortLabel";

import { logout } from "../../../Redux/Actions/userActions";
import Loading from "./../../../Components/Interactive/Loading";

// Component Imports
import SelfOptOutTableBody from "./SelfOptOutTableBody";
import SelfOptOutSearchForm from "./SelfOptOutSearchForm";
import Paginate from "../../../Utilities/Paginate";
import SuccessAlert from "./../../../Components/Interactive/SuccessAlert";
import FailAlert from "./../../../Components/Interactive/FailAlert";
import { Stack } from "@mui/material";
import { Button } from "@mui/material";

const SelfOptOut = ({ isOpen }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [selfOptOutSearchResults, setSelfOptOutSearchResults] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [loading, setIsLoading] = useState(false);
  const userAuth = useSelector((state) => state.auth);
  const isAdmin = userAuth?.userInfo?.userData?.role === "Administrator" || userAuth?.userInfo?.userData?.role === "Super User";

  const [email, setEmail] = useState("");
  const [brandId, setbrandId] = useState("");
  const [reportToken, setReportToken] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [ipAddress, setIpAddress] = useState("");
  const [createdStartDate, setCreatedStartDate] = useState(moment(new Date()).subtract(5, 'days').format("yyyy-MM-DD"));
  const [createdEndDate, setCreatedEndDate] = useState(undefined);
  const [showOnlyVerified, setShowOnlyVerified] = useState(true);
  const [approvedStatusId, setApprovedStatusId] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // for pagination
  const DefaultPageNumber = 1;
  const DefaultPageSize = 50;
  const [totalCount, setTotalCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(DefaultPageNumber);
  const [pageSize, _setPageSize] = useState(DefaultPageSize);
  const setPageSize = (val) => {
    if(val <= 0){
      val = DefaultPageSize;
    }
    _setPageSize(val);
  }

  const selectForBulk = async (id) => {
    let newSelectedIds = [...selectedIds];
    const index = newSelectedIds.indexOf(id);

    if(index > -1)
    {
      newSelectedIds.splice(index, 1);
    } else{
      newSelectedIds.push(id);
    }

    setSelectedIds(newSelectedIds);
  }

  const [sort, _setSort] = useState("-CreatedDate");
  const setSort = (val) => {
    _setSort(val);
    setPageNumber(DefaultPageNumber);
    handleSelfOptOutSearch(
      brandId,
      email,
      reportToken,
      firstName,
      lastName,
      ipAddress,
      createdStartDate,
      createdEndDate,
      approvedStatusId,
      showOnlyVerified,
      val,
      DefaultPageNumber,
      pageSize
    )
  };

  const tableHeaders = [
    {asc: "Email", desc: "-Email", label: "Email"},
    {asc: "BrandId", desc: "-BrandId", label: "Brand"},
    {asc: "ReportToken", desc: "-ReportToken", label: "Report Token"},
    {asc: "FirstName", desc: "-FirstName", label: "Name"},
    {asc: "Age", desc: "-Age", label: "Age"},
    {asc: "Location", desc: "-Location", label: "Location"},
    {asc: "IpAddress", desc: "-IpAddress", label: "IP Address"},
    {asc: "CreatedDate", desc: "-CreatedDate", label: "Requested Date"},
    {asc: "VerifiedDate", desc: "-VerifiedDate", label: "Verified Date"},
  ]

  useEffect(() => {
    (async () => {
      const res = await fetch(`/api/auth/get-auth`, {
        method: "GET",
        credentials: "include",
      });
      if (res.status === 401) {
        dispatch(logout());
        return navigate("/");
      }
    })();
  }, [dispatch, navigate]);

  const handleSortingChange = (_e, row) => {
    if(sort === row.desc){
      setSort(row.asc);
    }
    else if(sort === row.asc){
      setSort(row.desc);
    }
    else
      setSort(row.asc);
  };

  const BulkApprove = async () => {
    clearMessages();
    const res = await fetch(`/api/optouts/approve`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ optOutIds: selectedIds }),
    });
    const response = await res.json();
    if (response.success) {
      showSuccessMessage("Done: Opt-Outs Approved successfully");
      setTimeout(() => clearMessages(), 5000);
    } else {
      console.error("error", response);
      showErrorMessage("Error: " + response.error);
      setTimeout(() => clearMessages(), 5000);
    }
    // Redo search
    handleSelfOptOutSearch(
      brandId,
      email,
      reportToken,
      firstName,
      lastName,
      ipAddress,
      createdStartDate,
      createdEndDate,
      approvedStatusId,
      showOnlyVerified,
      sort,
      DefaultPageNumber,
      pageSize ?? DefaultPageSize
    );
    setSelectedIds([]);
  };

  const BulkIgnore = async () => {
    clearMessages();
    const res = await fetch(`/api/optouts/ignore`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ optOutIds: selectedIds }),
    });
    const response = await res.json();
    if (response.success) {
      showSuccessMessage("Done: Opt-Outs Ignored successfully");
      setTimeout(() => clearMessages(), 5000);
    } else {
      console.error("error", response);
      showErrorMessage("Error: " + response.error);
      setTimeout(() => clearMessages(), 5000);
    }
    // Redo search
    handleSelfOptOutSearch(
      brandId,
      email,
      reportToken,
      firstName,
      lastName,
      ipAddress,
      createdStartDate,
      createdEndDate,
      approvedStatusId,
      showOnlyVerified,
      sort,
      DefaultPageNumber,
      pageSize ?? DefaultPageSize
    );
    setSelectedIds([]);
  };

  const SelectAll = async () => {
    let toggleSelectAll = !selectAll;
    let newSelectedIds = [];

    if(toggleSelectAll)
    {   
      if(selfOptOutSearchResults){
        for (var i = 0; i < selfOptOutSearchResults.length; i++)
        {
          newSelectedIds.push(selfOptOutSearchResults[i].id);
        }  
      }    
    }
    setSelectAll(toggleSelectAll);
    setSelectedIds(newSelectedIds);
  }

  const clearMessages = async () => {
    setSuccessMessage(null);
    setErrorMessage(null);
  };
  const showSuccessMessage = async (message) => {
    setErrorMessage(null);
    setSuccessMessage(message);
  };
  const showErrorMessage = async (message) => {
    setSuccessMessage(null);
    setErrorMessage(message);
  };

  const handleSelfOptOutSearch = async (
    brandId,
    email,
    reportToken,
    firstName,
    lastName,
    ipAddress,
    createdStartDate,
    createdEndDate,
    approvedStatusId,
    showOnlyVerified,
    sort,
    pageNumberLocal,
    pageSizeLocal
  ) => {
    setSelfOptOutSearchResults(null);
    setIsLoading(true)

    const options = {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        brandId,
        email,
        reportToken,
        firstName,
        lastName,
        ipAddress,
        createdStartDate,
        createdEndDate,
        approvedStatusId: parseInt(approvedStatusId) < 0 ? "" : approvedStatusId,
        showOnlyVerified: showOnlyVerified ? true : undefined,
        sort: sort,
        pageNumber: pageNumberLocal,
        pageSize: pageSizeLocal
      }),
    };
    const res = await fetch("/api/optouts/search", options);
    setIsLoading(false)

    if (res.status === 401) {
      dispatch(logout());
      return navigate("/");
    }
    const {totalCount, pageNumber, pageSize, result, success} = await res.json();

    if (result?.errorMessage) {
      setErrorMessage(result.errorMessage);
      setTimeout(() => {
        setErrorMessage(null);
        setSelfOptOutSearchResults(null);
        setTotalCount(0);
        setPageNumber(1);
        setSelectedIds([]);
      }, 5000);
      return;
    }
    else if (!success) {
      setErrorMessage("Failed to send opt out search");
      setTimeout(() => {
        setErrorMessage(null);
        setSelfOptOutSearchResults(null);
        setTotalCount(0);
        setPageNumber(1);
        setSelectedIds([]);
      }, 5000);
      return;
    } else {
      setSelfOptOutSearchResults(result);
      setTotalCount(totalCount);
      setPageNumber(pageNumber);
      setPageSize(pageSize);
      setSelectedIds([]);
    }
  };

  const handleChangePage = (_e, newPage) => {
    var newPageNumber = newPage + 1;
    setPageNumber(newPageNumber);
    handleSelfOptOutSearch(
      brandId,
      email,
      reportToken,
      firstName,
      lastName,
      ipAddress,
      createdStartDate,
      createdEndDate,
      approvedStatusId,
      showOnlyVerified,
      sort,
      newPageNumber,
      pageSize
    );
  };

  const handleChangeRowsPerPage = (event) => {
    var newPageSize = parseInt(event.target.value, 10);
    setPageSize(newPageSize);
    setPageNumber(DefaultPageNumber);
    handleSelfOptOutSearch(
      brandId,
      email,
      reportToken,
      firstName,
      lastName,
      ipAddress,
      createdStartDate,
      createdEndDate,
      approvedStatusId,
      showOnlyVerified,
      sort,
      DefaultPageNumber,
      newPageSize
    );
  };

  // Dont show the page if the user is not an admin
  if(!isAdmin)
  {
    return (<Navigate to={{pathname: "/",}}/>);
  }

  return (
    <div className={`${isOpen ? "page" : "page-closed"} `}>
      <Stack direction="row" spacing={2}>
      <h3 className="heading">Self Opt Out</h3>
        <Button variant="text" style={{color: "white"}} size="small" href="/ManageOptOutBlacklist">Blacklist domains</Button>
        <Button variant="text" style={{color: "white"}} size="small" href="/ManageOptOutWhitelist">Whitelist emails</Button>
      </Stack>
      <div
        className="search-form-box"
        style={{
          background: "#1137535e",
          padding: "1rem",
          borderRadius: "10px",
        }}
      >
      <h3 className="heading" style={{ fontSize: "1.5rem", width: "100%" }}>
        Filters
      </h3>

      <br />
      <br />

      {SelfOptOutSearchForm(
        brandId, setbrandId,
        email, setEmail,
        reportToken, setReportToken,
        firstName, setFirstName,
        lastName, setLastName,
        ipAddress, setIpAddress,
        createdStartDate, setCreatedStartDate,
        createdEndDate, setCreatedEndDate,
        approvedStatusId, setApprovedStatusId,
        showOnlyVerified, setShowOnlyVerified
      )}

      <div>
        <button
          className="add-note-btn-open"
          onClick={() =>
            handleSelfOptOutSearch(
              brandId,
              email,
              reportToken,
              firstName,
              lastName,
              ipAddress,
              createdStartDate,
              createdEndDate,
              approvedStatusId,
              showOnlyVerified,
              sort,
              DefaultPageNumber,
              pageSize ?? DefaultPageSize
            )
          }
        >
          <span style={{ color: "#fff" }}>Search</span>
        </button>
      </div>
    </div>

      {loading ? (
        <div className="loading-widget">
          <Loading />
        </div>
      ) : (
        <TableContainer
            className="customer-order-table"
            style={{ marginBottom: "20px" }}
          >
            <div style={{ marginBottom: "5px", marginTop: "5px", float: "left" }}>
              <button className="add-note-btn-open" style={{ marginRight: "5px" }} onClick={() => SelectAll()}>
                {!selectAll && <span style={{ color: "#fff" }}>Select All</span>}
                {selectAll && <span style={{ color: "#fff" }}>Deselect All</span>}
              </button>
              {selectedIds.length > 0 && (<button className="add-note-btn-open" style={{ marginRight: "5px" }} onClick={() => BulkApprove()}>
                <span style={{ color: "#fff" }}>Bulk Approve</span>
              </button>)}
              {selectedIds.length > 0 && (<button className="add-note-btn-open" onClick={() => BulkIgnore()}>
                <span style={{ color: "#fff" }}>Bulk Ignore</span>
              </button>)}
            </div>
            <div style={{ marginBottom: "5px", marginTop: "5px", float: "right" }}>
              <h4 style={{color: "white", float: "right"}}>Total records found: {totalCount}</h4>
            </div>
            <Table aria-label="collapsible table" style={{ width: "100%" }}>
              <TableHead
                style={{
                  backgroundColor: "#FDFAF6",
                  borderRadius: "10px !important",
                }}
              >
                <TableRow
                  style={{
                    backgroundColor: "#0D2B41",
                  }}
                >
                  <TableCell
                    className="headerText noto-sans-font"
                    align="center"
                    padding="normal"
                  >
                      Select
                  </TableCell>
                  {tableHeaders.map(row => (
                    <TableCell
                      key={row.asc}
                      className="headerText"
                      align="center"
                      padding="normal"
                    >
                      <TableSortLabel
                        active={sort === row.asc || sort === row.desc ? true : false }
                        direction={sort === row.desc ? "desc" : "asc" }
                        onClick={e => handleSortingChange(e, row)}
                      >
                        {row.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell align="center" className="headerText" key="empty">
                  </TableCell>
                </TableRow>
              </TableHead>
              <SelfOptOutTableBody
                selfOptOutData={selfOptOutSearchResults}
                selectForBulk = {selectForBulk}
                selectAll = {selectAll}
              />
            </Table>

            { totalCount > pageSize ?
              <Paginate
                totalRowCount={totalCount}
                page={pageNumber-1}
                rowsPerPage={pageSize}
                handleChange={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                showCorePaginate={false}
                rowsPerPageOptions={[DefaultPageSize, 100, 300, 500]}
              />
              : null
            }

          </TableContainer>
      )}
      {
        successMessage ?
          (<div style={{ position: "fixed", top: "20px", right: "20px" }}>
            <SuccessAlert className="alert-animation" >
              {successMessage}
            </SuccessAlert>
          </div>
          )
        :
        errorMessage ?
          (<div style={{ position: "fixed", top: "20px", right: "20px" }}>
            <FailAlert>{errorMessage}</FailAlert>
          </div>)
          : null
      }
    </div>
  );
};

export default SelfOptOut;
